import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { FileText, HelpCircle, Link2, FileType2, FolderClosed, Search as SearchIcon, CornerDownLeft, MessageSquarePlus } from "lucide-react";

interface Category {
    id: string;
    name: string;
    color: string | null;
}

interface SearchResult {
    resultType: "category" | "page" | "question" | "link" | "document";
    id: string;
    title: string | null;
    snippet: string | null;
    categoryId: string | null;
    createdAt: string;
}

interface Props {
    query: string;
    categories: Category[];
    onClose: () => void;
    onAdvanced: () => void;
    onAskQuestion: () => void;
    onSelectPage: (pageId: string, categoryId: string) => void;
    onSelectQuestion: (questionId: string) => void;
    onSelectCategory: (categoryId: string) => void;
}

export default function SearchDropdown({
    query,
    categories,
    onClose,
    onAdvanced,
    onAskQuestion,
    onSelectPage,
    onSelectQuestion,
    onSelectCategory,
}: Props) {
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<SearchResult[]>([]);
    const [highlight, setHighlight] = useState(0);
    const abortRef = useRef<AbortController | null>(null);

    const categoriesById = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);
    const trimmed = query.trim();

    useEffect(() => {
        if (trimmed.length < 2) {
            setResults([]);
            setLoading(false);
            return;
        }
        const handle = setTimeout(() => {
            abortRef.current?.abort();
            const ctrl = new AbortController();
            abortRef.current = ctrl;
            setLoading(true);
            axios.get(`/api/manuals/search?q=${encodeURIComponent(trimmed)}&take=10`, { signal: ctrl.signal })
                .then(res => {
                    if (res.data.success) setResults(res.data.items);
                    else setResults([]);
                })
                .catch(err => {
                    if (axios.isCancel(err) || err.name === "CanceledError") return;
                    setResults([]);
                })
                .finally(() => setLoading(false));
        }, 200);
        return () => {
            clearTimeout(handle);
            abortRef.current?.abort();
        };
    }, [trimmed]);

    const pinned: { key: string; label: string; icon: React.ReactNode; onClick: () => void }[] = [];
    if (trimmed.length > 0) {
        pinned.push({
            key: "advanced",
            label: `Advanced search for "${trimmed}"`,
            icon: <SearchIcon size={14} />,
            onClick: onAdvanced,
        });
    }
    pinned.push({
        key: "ask",
        label: trimmed ? `Ask a question about "${trimmed}"` : "Ask a question",
        icon: <MessageSquarePlus size={14} />,
        onClick: onAskQuestion,
    });

    const allRows = [...pinned, ...results.map(r => ({ key: `${r.resultType}-${r.id}`, result: r }))];

    useEffect(() => {
        setHighlight(h => Math.min(h, Math.max(0, allRows.length - 1)));
    }, [allRows.length]);

    useEffect(() => {
        const h = (e: KeyboardEvent) => {
            if (e.key === "Escape") { onClose(); return; }
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlight(v => Math.min(v + 1, allRows.length - 1));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlight(v => Math.max(v - 1, 0));
            } else if (e.key === "Enter") {
                const row = allRows[highlight];
                if (!row) return;
                e.preventDefault();
                invokeRow(row);
            }
        };
        document.addEventListener("keydown", h);
        return () => document.removeEventListener("keydown", h);
    }, [allRows, highlight]);

    function invokeRow(row: { key: string; onClick?: () => void; result?: SearchResult }) {
        if (row.onClick) { row.onClick(); return; }
        if (!row.result) return;
        const r = row.result;
        if (r.resultType === "page") {
            onSelectPage(r.id, r.categoryId || "");
        } else if (r.resultType === "question") {
            onSelectQuestion(r.id);
        } else if (r.resultType === "category") {
            onSelectCategory(r.id);
        } else {
            onAdvanced();
        }
        onClose();
    }

    return (
        <div className="m-search-dropdown" role="listbox">
            <div className="m-search-dd-section">
                {pinned.map((row, i) => (
                    <button
                        key={row.key}
                        className={`m-search-dd-row pinned${highlight === i ? " active" : ""}`}
                        onMouseEnter={() => setHighlight(i)}
                        onMouseDown={e => { e.preventDefault(); invokeRow(row); }}
                    >
                        <span className="m-search-dd-icon">{row.icon}</span>
                        <span className="m-search-dd-text">{row.label}</span>
                        <span className="m-search-dd-hint"><CornerDownLeft size={11} /></span>
                    </button>
                ))}
            </div>

            <div className="m-search-dd-divider" />

            {trimmed.length < 2 ? (
                <div className="m-search-dd-empty">Type at least 2 characters to search.</div>
            ) : loading ? (
                <div className="m-search-dd-empty">Searching…</div>
            ) : results.length === 0 ? (
                <div className="m-search-dd-empty">No matches for "{trimmed}".</div>
            ) : (
                <div className="m-search-dd-section">
                    {results.map((r, i) => {
                        const idx = pinned.length + i;
                        const cat = r.resultType !== "category" && r.categoryId ? categoriesById.get(r.categoryId) : null;
                        return (
                            <button
                                key={`${r.resultType}-${r.id}`}
                                className={`m-search-dd-row${highlight === idx ? " active" : ""}`}
                                onMouseEnter={() => setHighlight(idx)}
                                onMouseDown={e => { e.preventDefault(); invokeRow({ key: "", result: r }); }}
                            >
                                <span className="m-search-dd-icon">{iconFor(r.resultType)}</span>
                                <span className="m-search-dd-text">
                                    <span className="m-search-dd-title">{r.title || "(untitled)"}</span>
                                    <span className="m-search-dd-meta">
                                        <span className="m-search-dd-type">{typeLabel(r.resultType)}</span>
                                        {cat && <>
                                            <span> · </span>
                                            <span style={{ color: cat.color || undefined }}>{cat.name}</span>
                                        </>}
                                        {r.snippet && <><span> · </span><span className="m-search-dd-snippet">{truncate(stripHtml(r.snippet), 80)}</span></>}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function iconFor(type: string): React.ReactNode {
    if (type === "category") return <FolderClosed size={14} color="#3B82F6" />;
    if (type === "page") return <FileText size={14} color="#1e85e5" />;
    if (type === "question") return <HelpCircle size={14} color="#dc2626" />;
    if (type === "link") return <Link2 size={14} color="#64748b" />;
    if (type === "document") return <FileType2 size={14} color="#64748b" />;
    return <FileText size={14} />;
}

function typeLabel(type: string): string {
    if (type === "category") return "Category";
    if (type === "page") return "Page";
    if (type === "question") return "Question";
    if (type === "link") return "Link";
    if (type === "document") return "Document";
    return type;
}

function truncate(s: string, max: number): string {
    if (!s) return "";
    if (s.length <= max) return s;
    return s.slice(0, max).trimEnd() + "…";
}

function stripHtml(s: string): string {
    if (!s) return "";
    return s.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}
